import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMemo } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { ClipboardList, Star, TrendingUp, Users } from 'lucide-react';
import { decimal, formatDate } from '@/shared/lib/format';
import { chartAxisTick, chartLegendStyle, chartTooltipProps } from '@/shared/lib/chart-theme';
import { PageHeader } from '@/shared/components/page-header';
import { KpiCard } from '@/shared/components/kpi-card';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';
import {
  capitalizeFirst,
  npsZone,
  SURVEY_STATUS_LABEL,
  SURVEY_STATUS_VARIANT,
} from '../model/nps-utils';
import { useAnswerData, useAnswerMonthlyTotals, useOrgRanking, useSurveyRanking, useSurveys } from '../api/use-nps';
import { useNpsAdmin } from '../hooks/use-nps-access';

export function NpsHomePage() {
  const { isAdmin, me } = useNpsAdmin();
  const surveysQuery = useSurveys();
  const surveysAll = surveysQuery.data ?? [];
  const surveys = isAdmin
    ? surveysAll
    : surveysAll.filter((s) => s.user?.id === me?.id || s.user?.email === me?.email);
  const scopedSurveyIds = useMemo(() => surveys.map((s) => s.id), [surveys]);
  const scopedIdSet = useMemo(() => new Set(scopedSurveyIds), [scopedSurveyIds]);

  // Não-admin: charts só com as mesmas pesquisas dos cards/KPIs. Admin: todas.
  const chartSurveyIds = isAdmin ? undefined : scopedSurveyIds;
  const monthlyQuery = useAnswerMonthlyTotals(chartSurveyIds);
  const enrichedQuery = useAnswerData(chartSurveyIds);
  const surveyRankingQuery = useSurveyRanking();
  const orgRankingQuery = useOrgRanking(isAdmin);

  const monthlyRaw = monthlyQuery.data ?? {};
  const enriched = enrichedQuery.data ?? [];
  const surveyRanking = useMemo(() => {
    const rows = surveyRankingQuery.data ?? [];
    return isAdmin ? rows : rows.filter((r) => scopedIdSet.has(r.id));
  }, [surveyRankingQuery.data, isAdmin, scopedIdSet]);
  const orgRanking = orgRankingQuery.data ?? [];

  const monthlyChart = Object.entries(monthlyRaw)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, value]) => ({ month, value: Number(value) }));

  const totalAnswers = surveys.reduce((acc, s) => acc + (s.answers_count ?? 0), 0);
  const active = surveys.filter((s) => s.status === 'ACTIVE').length;
  const avgNps = surveyRanking.length
    ? Math.round(surveyRanking.reduce((a, r) => a + r.nps_score, 0) / surveyRanking.length)
    : 0;

  const totals = enriched.reduce(
    (acc, d) => ({ promoters: acc.promoters + d.promoters, passives: acc.passives + d.passives, detractors: acc.detractors + d.detractors }),
    { promoters: 0, passives: 0, detractors: 0 },
  );
  const pie = [
    { name: 'Promotores', value: totals.promoters, color: 'hsl(var(--success))' },
    { name: 'Neutros', value: totals.passives, color: 'hsl(var(--warning))' },
    { name: 'Detratores', value: totals.detractors, color: 'hsl(var(--destructive))' },
  ];

  const loading = surveysQuery.isLoading || monthlyQuery.isLoading;

  return (
    <>
      <PageHeader title="Visão Geral — NPS" description="Acompanhe a satisfação dos seus clientes." />

      <div className="mb-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Pesquisas" value={loading ? '…' : decimal(surveys.length)} icon={ClipboardList} loading={loading} index={0} />
        <KpiCard label="Pesquisas ativas" value={loading ? '…' : decimal(active)} icon={Star} loading={loading} index={1} />
        <KpiCard label="Respostas" value={loading ? '…' : decimal(totalAnswers)} icon={Users} loading={loading} index={2} />
        <KpiCard label="NPS médio" value={loading ? '…' : String(avgNps)} icon={TrendingUp} loading={loading} index={3} hint="Média do NPS das pesquisas (-100 a 100)." />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold">Respostas por mês</h2>
          {monthlyQuery.isLoading ? (
            <Skeleton className="h-72 w-full rounded-xl" />
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <AreaChart data={monthlyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => capitalizeFirst(format(new Date(v), 'MMM', { locale: ptBR }))}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <RTooltip {...chartTooltipProps} />
                <Area dataKey="value" name="Total" type="monotone" fill="hsl(var(--primary)/0.25)" stroke="hsl(var(--primary))" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-sm font-semibold">Distribuição NPS</h2>
          {enrichedQuery.isLoading ? (
            <Skeleton className="h-72 w-full rounded-xl" />
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {pie.map((p) => <Cell key={p.name} fill={p.color} />)}
                </Pie>
                <Legend wrapperStyle={chartLegendStyle} />
                <RTooltip {...chartTooltipProps} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-semibold">Ranking por pesquisa (NPS)</h2>
          {surveyRankingQuery.isLoading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
          ) : surveyRanking.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados ainda.</p>
          ) : (
            <ul className="divide-y divide-border/40">
              {surveyRanking.map((r, i) => {
                const zone = npsZone(r.nps_score);
                return (
                  <li key={r.id} className="flex items-center gap-3 py-3">
                    <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-semibold text-primary">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.title}</p>
                      <p className="text-xs text-muted-foreground">{decimal(r.answers_count)} respostas</p>
                    </div>
                    <Badge variant={SURVEY_STATUS_VARIANT[r.status]} className="font-normal">{SURVEY_STATUS_LABEL[r.status]}</Badge>
                    <span className={`text-lg font-semibold tabular-nums ${zone.className}`}>{r.nps_score}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {isAdmin ? (
          <Card className="p-5">
            <h2 className="mb-4 text-sm font-semibold">Ranking por organização</h2>
            {orgRankingQuery.isLoading ? (
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
            ) : orgRanking.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem dados ainda.</p>
            ) : (
              <ul className="divide-y divide-border/40">
                {orgRanking.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.user?.name} {r.user?.last_name}</p>
                      <p className="text-xs text-muted-foreground">{r.total_answers} respostas · desde {formatDate(r.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : (
          <Card className="p-5">
            <h2 className="mb-4 text-sm font-semibold">Detratores vs promotores (mensal)</h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={enriched}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={chartAxisTick} axisLine={false} tickLine={false} />
                <YAxis tick={chartAxisTick} axisLine={false} tickLine={false} />
                <Legend wrapperStyle={chartLegendStyle} />
                <RTooltip {...chartTooltipProps} />
                <Bar dataKey="promoters" name="Promotores" stackId="a" fill="hsl(var(--success))" />
                <Bar dataKey="passives" name="Neutros" stackId="a" fill="hsl(var(--warning))" />
                <Bar dataKey="detractors" name="Detratores" stackId="a" fill="hsl(var(--destructive))" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </>
  );
}
